/**
 * ProductsToolbar Section Component
 *
 * Renders the Filters button and New Product. Every filter — format included —
 * lives in the Filters modal, so the badge here is the page's single count of
 * what is applied.
 */

// UI primitives
import { Counter } from '@/components/ui/counter';
import { Button } from '@/components/ui/button';

// Icons
import { FilterIcon, PlusIcon } from '../../icons';

// Hooks and utilities
import { useTranslation } from '../../../hooks/useTranslation';

// Types
import type { ProductsToolbarProps } from '../types';

export function ProductsToolbar({
  onFiltersClick,
  onNewProductClick,
  filterCount = 0,
  canWrite = false,
  className,
}: ProductsToolbarProps) {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <div className="flex items-center gap-4">
        {/* Filters button */}
        <div className="relative">
          <Button
            variant="outline-light"
            onClick={onFiltersClick}
          >
            <FilterIcon className="w-3 h-3.5" aria-hidden="true" />
            <span>{t('toolbar.filters')}</span>
          </Button>
          {filterCount > 0 && (
            <Counter
              value={filterCount}
              className="absolute -top-1.5 -right-1.5"
              aria-label={t('toolbar.filter_count', { count: filterCount })}
            />
          )}
        </div>

        {/* New Product button — RBAC: hidden for read-only users (write). */}
        {canWrite && (
          <Button onClick={onNewProductClick ?? (() => {})}>
            <PlusIcon className="size-5" aria-hidden="true" />
            {t('toolbar.new_product')}
          </Button>
        )}
      </div>
    </div>
  );
}
